import { useCallback, useEffect, useState } from "react";
import { HandHelping, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AdminVolunteers({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/volunteers", { headers });
      setItems(data);
    } catch {
      toast.error("Failed to load volunteer EOIs");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (entry) => {
    if (!window.confirm(`Remove ${entry.name} from the volunteer list?`)) return;
    try {
      await api.delete(`/admin/volunteers/${entry.id}`, { headers });
      setItems((prev) => prev.filter((i) => i.id !== entry.id));
      toast.success("Removed");
    } catch {
      toast.error("Couldn't remove entry");
    }
  };

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-clay-orange" /></div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <section>
        <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight mb-5">
          Volunteer EOIs <span className="text-clay-muted text-xl">({items.length})</span>
        </h2>
        <div className="border border-clay-border overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-clay-border bg-clay-surface font-mono text-[10px] uppercase tracking-[0.2em] text-clay-muted text-left">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Areas</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-clay-muted font-mono text-xs uppercase tracking-[0.2em]">
                    No volunteer EOIs yet — the form is near the top of the homepage
                  </td>
                </tr>
              )}
              {items.map((v) => (
                <tr key={v.id} data-testid="volunteer-row" className="border-b border-clay-border/50 hover:bg-clay-card/60 transition-colors">
                  <td className="px-4 py-3 font-display font-bold uppercase tracking-wide">{v.name}</td>
                  <td className="px-4 py-3 font-mono"><a href={`tel:${v.phone}`} className="text-clay-gold hover:underline">{v.phone || "—"}</a></td>
                  <td className="px-4 py-3"><a href={`mailto:${v.email}`} className="text-clay-gold hover:underline">{v.email}</a></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {(v.areas || []).map((a) => (
                        <span key={a} className="font-mono text-[10px] uppercase tracking-[0.15em] border border-clay-border text-clay-muted px-2 py-0.5">
                          {a.replace(" (Speedway Australia insurance required)", "")}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-clay-muted">{v.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid="volunteer-delete-button"
                      onClick={() => remove(v)}
                      className="text-clay-muted hover:text-red-400 transition-colors p-1.5"
                      aria-label="Remove entry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted mt-4 flex items-center gap-2">
          <HandHelping className="w-4 h-4" /> Every volunteer EOI is also emailed to the club
        </p>
      </section>
    </div>
  );
}
